/**
 * SpeedDialErrorBoundary Component
 *
 * Error boundary specifically for speed dial components.
 * Provides graceful error handling and recovery.
 */
import React, { Component, ErrorInfo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { logger } from '../../utils/logger';
import { logClientErrorFromInnerBoundary } from '../../utils/reportClientIncident';

interface SpeedDialErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface SpeedDialErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class SpeedDialErrorBoundary extends Component<SpeedDialErrorBoundaryProps, SpeedDialErrorBoundaryState> {
  constructor(props: SpeedDialErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SpeedDialErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('SpeedDial Error:', error, { errorInfo });
    logClientErrorFromInnerBoundary(error, 'SpeedDialErrorBoundary');
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Return null to hide speed dial on error - graceful degradation
      // Speed dial is not critical for app functionality
      if (import.meta.env.DEV) {
        return (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              right: 'auto',
              zIndex: (theme) => theme.zIndex.speedDial,
              p: 2,
              bgcolor: 'error.light',
              borderRadius: 2,
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              boxShadow: 3,
            }}
          >
            <Typography variant="body2" color="error" gutterBottom>
              Speed Dial Error
            </Typography>
            <Typography variant="caption" color="text.secondary" paragraph>
              {this.state.error?.message || 'An error occurred'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
            >
              Retry
            </Button>
          </Box>
        );
      }
      // In production, silently fail - speed dial is not critical
      return null;
    }

    return this.props.children;
  }
}

export default SpeedDialErrorBoundary;
